import { useState, useCallback } from 'react';
import type { DoctorShift } from '../../../../types/doctorShift';

/**
 * ソートキーの型定義
 */
export type SortKey = 'doctorName' | 'startTime';

/**
 * ソート順序の型定義
 */
export type SortOrder = 'asc' | 'desc';

/**
 * ソート状態の型定義
 */
export interface SortState {
  /** ソートキー */
  sortBy: SortKey;
  /** ソート順序 */
  sortOrder: SortOrder;
}

/**
 * ソートフックの戻り値型
 */
export interface UseShiftSortReturn {
  /** 現在のソート状態 */
  sortState: SortState;
  /** ソートキーを変更 */
  setSortBy: (key: SortKey) => void;
  /** ソート順序をトグル */
  toggleSortOrder: () => void;
  /** ソートを適用 */
  sortShifts: (shifts: DoctorShift[]) => DoctorShift[];
}

/**
 * ソート初期状態
 */
const INITIAL_SORT_STATE: SortState = {
  sortBy: 'startTime',
  sortOrder: 'asc',
};

/**
 * シフトソート用カスタムフック
 *
 * @description
 * REQ-006に基づくソート機能を提供する。
 * 医師名、開始時間でのソートをサポート。
 *
 * @returns {UseShiftSortReturn} ソート状態とアクション
 *
 * @example
 * const { sortState, setSortBy, sortShifts } = useShiftSort();
 * const sortedShifts = sortShifts(shifts);
 */
export const useShiftSort = (): UseShiftSortReturn => {
  const [sortState, setSortState] = useState<SortState>(INITIAL_SORT_STATE);

  /**
   * ソートキーを変更
   * 同じキーが選択された場合は順序をトグル
   */
  const setSortBy = useCallback((key: SortKey) => {
    setSortState((prev) => {
      if (prev.sortBy === key) {
        // 同じキーの場合は順序をトグル
        return { ...prev, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' };
      }
      // 新しいキーの場合は昇順でリセット
      return { sortBy: key, sortOrder: 'asc' };
    });
  }, []);

  /**
   * ソート順序をトグル
   */
  const toggleSortOrder = useCallback(() => {
    setSortState((prev) => ({
      ...prev,
      sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  /**
   * ソートを適用
   */
  const sortShifts = useCallback(
    (shifts: DoctorShift[]): DoctorShift[] => {
      const sorted = [...shifts].sort((a, b) => {
        let comparison = 0;

        switch (sortState.sortBy) {
          case 'doctorName': {
            const nameA =
              a.doctor_full_name || `${a.doctor_last_name || ''}${a.doctor_first_name || ''}`;
            const nameB =
              b.doctor_full_name || `${b.doctor_last_name || ''}${b.doctor_first_name || ''}`;
            comparison = nameA.localeCompare(nameB, 'ja');
            break;
          }
          case 'startTime': {
            comparison = a.start_time.localeCompare(b.start_time);
            break;
          }
        }

        return sortState.sortOrder === 'asc' ? comparison : -comparison;
      });

      return sorted;
    },
    [sortState]
  );

  return {
    sortState,
    setSortBy,
    toggleSortOrder,
    sortShifts,
  };
};
